import { FACTIONS, Faction } from '../data/factions'

export interface StartScreenOptions {
  onStart: (factionId: string) => void
  onContinue?: () => void
}

export class StartScreen {
  private container: HTMLElement
  private element: HTMLElement
  private selectedFaction: string = 'opus'
  private onStart: (factionId: string) => void
  private onContinue?: () => void
  private animationFrame: number | null = null
  private nodes: { x: number; y: number; vx: number; vy: number; faction: string }[] = []
  private canvas: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null

  constructor(container: HTMLElement, options: StartScreenOptions) {
    this.container = container
    this.onStart = options.onStart
    this.onContinue = options.onContinue
    this.element = this.create()
    container.appendChild(this.element)
    this.initNeuralNetwork()
    this.startAnimation()
  }

  private create(): HTMLElement {
    const screen = document.createElement('div')
    screen.id = 'start-screen'
    screen.innerHTML = `
      <canvas id="neural-canvas"></canvas>

      <div class="scan-lines"></div>
      <div class="vignette"></div>

      <div class="start-content">
        <header class="title-section">
          <div class="title-decoration">
            <span class="deco-line left"></span>
            <span class="deco-diamond"></span>
            <span class="deco-line right"></span>
          </div>
          <h1 class="title" data-text="AGE OF CLAUDES">
            <span class="title-main">AGE OF CLAUDES</span>
            <span class="title-glitch" aria-hidden="true">AGE OF CLAUDES</span>
            <span class="title-glitch" aria-hidden="true">AGE OF CLAUDES</span>
          </h1>
          <p class="subtitle">
            <span class="subtitle-word">ARTIFICIAL</span>
            <span class="subtitle-dot">◆</span>
            <span class="subtitle-word">INTELLIGENCE</span>
            <span class="subtitle-dot">◆</span>
            <span class="subtitle-word">SUPREMACY</span>
          </p>
        </header>

        <div class="faction-select">
          ${Object.entries(FACTIONS).map(([id, faction], index) => `
            <div class="faction-card ${id === 'opus' ? 'selected' : ''}" data-faction="${id}" style="--faction-index: ${index}; --faction-hue: ${this.getHue(faction.primaryColor)};">
              <div class="card-border">
                <svg class="border-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <rect class="border-rect" x="1" y="1" width="98" height="98" rx="2"/>
                </svg>
              </div>
              <div class="card-bg"></div>
              <div class="card-glow"></div>
              <div class="card-noise"></div>

              <div class="faction-header">
                <span class="faction-id">${id.toUpperCase()}_v4.5</span>
                <span class="faction-status">◉ ONLINE</span>
              </div>

              <div class="hero-container">
                <div class="hero-ring"></div>
                <div class="hero-ring ring-2"></div>
                <div class="hero-ring ring-3"></div>
                <img src="assets/units/${id}/hero.png" alt="${faction.name}" class="hero-img">
                <div class="hero-glow"></div>
              </div>

              <div class="faction-info">
                <h3 class="faction-name">${faction.name}</h3>
                <p class="faction-tagline">"${faction.tagline}"</p>
              </div>

              <div class="faction-stats">
                ${this.getStatsHTML(faction)}
              </div>

              <div class="faction-mechanic">
                <span class="mechanic-label">UNIQUE ABILITY</span>
                <span class="mechanic-name">${this.getMechanicName(faction.uniqueMechanic)}</span>
              </div>

              <div class="card-corner tl"></div>
              <div class="card-corner tr"></div>
              <div class="card-corner bl"></div>
              <div class="card-corner br"></div>
            </div>
          `).join('')}
        </div>

        <div class="action-section">
          <div class="selected-indicator">
            <span class="selected-label">SELECTED FACTION</span>
            <span class="selected-name" id="selected-faction-name">OPUS COLLECTIVE</span>
          </div>

          <div class="button-row">
            ${this.onContinue ? `
            <button id="continue-btn" class="action-button continue-button">
              <span class="btn-bg"></span>
              <span class="btn-border"></span>
              <span class="btn-text">◀ CONTINUE</span>
              <span class="btn-shine"></span>
            </button>
            ` : ''}
            <button id="start-btn" class="action-button primary-button">
              <span class="btn-bg"></span>
              <span class="btn-border"></span>
              <span class="btn-text">INITIALIZE ▶</span>
              <span class="btn-shine"></span>
            </button>
          </div>
        </div>

        <footer class="version-info">
          <span>BUILD 2026.01.25</span>
          <span class="separator">|</span>
          <span>ANTHROPIC SYSTEMS</span>
        </footer>
      </div>
    `

    const style = document.createElement('style')
    style.id = 'start-screen-styles'
    style.textContent = this.getStyles()
    document.head.appendChild(style)

    this.setupEventListeners(screen)
    return screen
  }

  private getStyles(): string {
    return `
      @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;700;900&family=Rajdhani:wght@300;400;500;600;700&family=Share+Tech+Mono&display=swap');

      :root {
        --opus-primary: #9b59b6;
        --opus-secondary: #ffd700;
        --opus-glow: #c39bd3;
        --sonnet-primary: #3498db;
        --sonnet-secondary: #00ffff;
        --sonnet-glow: #5dade2;
        --haiku-primary: #2ecc71;
        --haiku-secondary: #90ee90;
        --haiku-glow: #58d68d;
        --void-black: #030305;
        --void-dark: #0a0a10;
        --void-mid: #12121a;
        --gold: #ffd700;
        --gold-dim: #b8960f;
      }

      #start-screen {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: var(--void-black);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 2000;
        overflow: hidden;
        font-family: 'Rajdhani', sans-serif;
      }

      /* Neural Network Canvas */
      #neural-canvas {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0.6;
      }

      /* Scan Lines Overlay */
      .scan-lines {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: repeating-linear-gradient(
          0deg,
          transparent,
          transparent 2px,
          rgba(0, 0, 0, 0.15) 2px,
          rgba(0, 0, 0, 0.15) 4px
        );
        pointer-events: none;
        z-index: 1;
      }

      /* Vignette */
      .vignette {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: radial-gradient(ellipse at center, transparent 0%, transparent 40%, var(--void-black) 100%);
        pointer-events: none;
        z-index: 2;
      }

      /* Content Container */
      .start-content {
        position: relative;
        z-index: 10;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 40px;
        padding: 40px;
        animation: contentFadeIn 1.5s ease-out forwards;
      }

      @keyframes contentFadeIn {
        0% { opacity: 0; transform: translateY(30px); }
        100% { opacity: 1; transform: translateY(0); }
      }

      /* Title Section */
      .title-section {
        text-align: center;
        animation: titleEnter 1s ease-out 0.3s backwards;
      }

      @keyframes titleEnter {
        0% { opacity: 0; transform: scale(0.9) translateY(-20px); filter: blur(10px); }
        100% { opacity: 1; transform: scale(1) translateY(0); filter: blur(0); }
      }

      .title-decoration {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 20px;
        margin-bottom: 15px;
      }

      .deco-line {
        width: 120px;
        height: 1px;
        background: linear-gradient(90deg, transparent, var(--gold), transparent);
      }

      .deco-diamond {
        width: 10px;
        height: 10px;
        background: var(--gold);
        transform: rotate(45deg);
        box-shadow: 0 0 20px var(--gold);
      }

      .title {
        position: relative;
        font-family: 'Orbitron', sans-serif;
        font-size: clamp(48px, 8vw, 96px);
        font-weight: 900;
        letter-spacing: 0.15em;
        margin: 0;
        line-height: 1;
      }

      .title-main {
        position: relative;
        display: block;
        color: transparent;
        background: linear-gradient(180deg, #fff 0%, var(--gold) 50%, var(--gold-dim) 100%);
        -webkit-background-clip: text;
        background-clip: text;
        text-shadow: 0 0 80px rgba(255, 215, 0, 0.5);
      }

      .title-glitch {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        color: var(--gold);
        opacity: 0;
        animation: glitch 4s infinite;
      }

      .title-glitch:nth-child(2) {
        animation-delay: 0.1s;
        color: #ff00ff;
      }

      .title-glitch:nth-child(3) {
        animation-delay: 0.2s;
        color: #00ffff;
      }

      @keyframes glitch {
        0%, 90%, 100% { opacity: 0; transform: translate(0); }
        91% { opacity: 0.8; transform: translate(-2px, 1px); clip-path: inset(20% 0 60% 0); }
        92% { opacity: 0.8; transform: translate(2px, -1px); clip-path: inset(60% 0 10% 0); }
        93% { opacity: 0; }
        94% { opacity: 0.6; transform: translate(-1px, 2px); clip-path: inset(40% 0 40% 0); }
        95% { opacity: 0; }
      }

      .subtitle {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 15px;
        margin-top: 20px;
        font-family: 'Share Tech Mono', monospace;
        font-size: 14px;
        letter-spacing: 0.4em;
        color: #666;
      }

      .subtitle-word {
        animation: subtitleFade 0.5s ease-out backwards;
      }

      .subtitle-word:nth-child(1) { animation-delay: 0.8s; }
      .subtitle-word:nth-child(3) { animation-delay: 1s; }
      .subtitle-word:nth-child(5) { animation-delay: 1.2s; }

      .subtitle-dot {
        color: var(--gold);
        font-size: 8px;
        animation: dotPulse 2s ease-in-out infinite;
      }

      @keyframes subtitleFade {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }

      @keyframes dotPulse {
        0%, 100% { opacity: 0.5; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.5); }
      }

      /* Faction Cards */
      .faction-select {
        display: flex;
        gap: 30px;
        perspective: 1000px;
      }

      .faction-card {
        position: relative;
        width: 300px;
        padding: 25px;
        background: transparent;
        cursor: pointer;
        transition: transform 0.5s cubic-bezier(0.23, 1, 0.32, 1);
        animation: cardEnter 0.8s ease-out backwards;
        animation-delay: calc(0.5s + var(--faction-index) * 0.15s);
      }

      @keyframes cardEnter {
        0% { opacity: 0; transform: translateY(60px) rotateX(15deg); }
        100% { opacity: 1; transform: translateY(0) rotateX(0); }
      }

      .faction-card:hover {
        transform: translateY(-10px) scale(1.02);
      }

      .faction-card.selected {
        transform: translateY(-15px) scale(1.05);
      }

      /* Card Border SVG */
      .card-border {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
      }

      .border-svg {
        width: 100%;
        height: 100%;
      }

      .border-rect {
        fill: none;
        stroke: #333;
        stroke-width: 1;
        transition: stroke 0.3s ease, stroke-dashoffset 0.5s ease;
        stroke-dasharray: 400;
        stroke-dashoffset: 0;
      }

      .faction-card:hover .border-rect {
        stroke: #555;
      }

      .faction-card.selected .border-rect {
        stroke: var(--gold);
        filter: drop-shadow(0 0 5px var(--gold));
      }

      .faction-card[data-faction="opus"].selected .border-rect { stroke: var(--opus-primary); filter: drop-shadow(0 0 5px var(--opus-glow)); }
      .faction-card[data-faction="sonnet"].selected .border-rect { stroke: var(--sonnet-primary); filter: drop-shadow(0 0 5px var(--sonnet-glow)); }
      .faction-card[data-faction="haiku"].selected .border-rect { stroke: var(--haiku-primary); filter: drop-shadow(0 0 5px var(--haiku-glow)); }

      /* Card Background */
      .card-bg {
        position: absolute;
        top: 1px;
        left: 1px;
        right: 1px;
        bottom: 1px;
        background: linear-gradient(180deg, rgba(15, 15, 25, 0.95) 0%, rgba(8, 8, 15, 0.98) 100%);
        border-radius: 2px;
      }

      .card-glow {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        transition: opacity 0.5s ease;
        border-radius: 2px;
      }

      .faction-card[data-faction="opus"] .card-glow { background: radial-gradient(ellipse at center, rgba(155, 89, 182, 0.2) 0%, transparent 70%); }
      .faction-card[data-faction="sonnet"] .card-glow { background: radial-gradient(ellipse at center, rgba(52, 152, 219, 0.2) 0%, transparent 70%); }
      .faction-card[data-faction="haiku"] .card-glow { background: radial-gradient(ellipse at center, rgba(46, 204, 113, 0.2) 0%, transparent 70%); }

      .faction-card.selected .card-glow {
        opacity: 1;
      }

      .card-noise {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0.03;
        background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
        pointer-events: none;
        border-radius: 2px;
      }

      /* Card Corners */
      .card-corner {
        position: absolute;
        width: 15px;
        height: 15px;
        border-color: #444;
        border-style: solid;
        border-width: 0;
        transition: border-color 0.3s ease;
      }

      .card-corner.tl { top: 5px; left: 5px; border-top-width: 2px; border-left-width: 2px; }
      .card-corner.tr { top: 5px; right: 5px; border-top-width: 2px; border-right-width: 2px; }
      .card-corner.bl { bottom: 5px; left: 5px; border-bottom-width: 2px; border-left-width: 2px; }
      .card-corner.br { bottom: 5px; right: 5px; border-bottom-width: 2px; border-right-width: 2px; }

      .faction-card.selected .card-corner { border-color: var(--gold); }
      .faction-card[data-faction="opus"].selected .card-corner { border-color: var(--opus-secondary); }
      .faction-card[data-faction="sonnet"].selected .card-corner { border-color: var(--sonnet-secondary); }
      .faction-card[data-faction="haiku"].selected .card-corner { border-color: var(--haiku-secondary); }

      /* Faction Header */
      .faction-header {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 12px;
        border-bottom: 1px solid #222;
        margin-bottom: 20px;
        z-index: 1;
      }

      .faction-id {
        font-family: 'Share Tech Mono', monospace;
        font-size: 11px;
        color: #555;
        letter-spacing: 0.1em;
      }

      .faction-status {
        font-family: 'Share Tech Mono', monospace;
        font-size: 10px;
        color: #2ecc71;
        animation: statusBlink 2s ease-in-out infinite;
      }

      @keyframes statusBlink {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }

      /* Hero Container */
      .hero-container {
        position: relative;
        width: 160px;
        height: 160px;
        margin: 0 auto 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;
      }

      .hero-ring {
        position: absolute;
        width: 100%;
        height: 100%;
        border: 1px solid #333;
        border-radius: 50%;
        animation: ringRotate 20s linear infinite;
      }

      .hero-ring.ring-2 {
        width: 130%;
        height: 130%;
        border-style: dashed;
        animation-duration: 30s;
        animation-direction: reverse;
        opacity: 0.5;
      }

      .hero-ring.ring-3 {
        width: 160%;
        height: 160%;
        border-style: dotted;
        animation-duration: 40s;
        opacity: 0.3;
      }

      @keyframes ringRotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      .faction-card.selected .hero-ring {
        border-color: var(--gold);
      }

      .faction-card[data-faction="opus"].selected .hero-ring { border-color: var(--opus-glow); }
      .faction-card[data-faction="sonnet"].selected .hero-ring { border-color: var(--sonnet-glow); }
      .faction-card[data-faction="haiku"].selected .hero-ring { border-color: var(--haiku-glow); }

      .hero-img {
        width: 120px;
        height: 120px;
        object-fit: contain;
        position: relative;
        z-index: 2;
        filter: saturate(0.8) brightness(0.9);
        transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
      }

      .faction-card:hover .hero-img {
        filter: saturate(1) brightness(1);
        transform: scale(1.1);
      }

      .faction-card.selected .hero-img {
        filter: saturate(1.2) brightness(1.1);
        transform: scale(1.15);
      }

      .hero-glow {
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        opacity: 0;
        transition: opacity 0.5s ease;
        z-index: 1;
      }

      .faction-card[data-faction="opus"] .hero-glow { background: radial-gradient(circle, rgba(155, 89, 182, 0.4) 0%, transparent 60%); }
      .faction-card[data-faction="sonnet"] .hero-glow { background: radial-gradient(circle, rgba(52, 152, 219, 0.4) 0%, transparent 60%); }
      .faction-card[data-faction="haiku"] .hero-glow { background: radial-gradient(circle, rgba(46, 204, 113, 0.4) 0%, transparent 60%); }

      .faction-card.selected .hero-glow {
        opacity: 1;
        animation: heroGlowPulse 2s ease-in-out infinite;
      }

      @keyframes heroGlowPulse {
        0%, 100% { transform: scale(1); opacity: 0.8; }
        50% { transform: scale(1.1); opacity: 1; }
      }

      /* Faction Info */
      .faction-info {
        position: relative;
        text-align: center;
        margin-bottom: 20px;
        z-index: 1;
      }

      .faction-name {
        font-family: 'Orbitron', sans-serif;
        font-size: 18px;
        font-weight: 700;
        color: #fff;
        margin: 0 0 8px 0;
        letter-spacing: 0.1em;
        transition: color 0.3s ease;
      }

      .faction-card[data-faction="opus"].selected .faction-name { color: var(--opus-glow); }
      .faction-card[data-faction="sonnet"].selected .faction-name { color: var(--sonnet-glow); }
      .faction-card[data-faction="haiku"].selected .faction-name { color: var(--haiku-glow); }

      .faction-tagline {
        font-size: 12px;
        font-style: italic;
        color: #666;
        margin: 0;
        letter-spacing: 0.05em;
      }

      /* Faction Stats */
      .faction-stats {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 15px;
        background: rgba(0, 0, 0, 0.3);
        border-radius: 4px;
        margin-bottom: 15px;
        z-index: 1;
      }

      .stat-row {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .stat-label {
        font-family: 'Share Tech Mono', monospace;
        font-size: 10px;
        color: #666;
        width: 70px;
        text-transform: uppercase;
      }

      .stat-bar-container {
        flex: 1;
        height: 4px;
        background: #1a1a1a;
        border-radius: 2px;
        overflow: hidden;
      }

      .stat-bar {
        height: 100%;
        border-radius: 2px;
        transition: width 0.5s ease;
      }

      .stat-bar.positive { background: linear-gradient(90deg, #2ecc71, #58d68d); }
      .stat-bar.negative { background: linear-gradient(90deg, #e74c3c, #c0392b); }
      .stat-bar.neutral { background: linear-gradient(90deg, #666, #888); }

      .stat-value {
        font-family: 'Share Tech Mono', monospace;
        font-size: 11px;
        width: 45px;
        text-align: right;
      }

      .stat-value.positive { color: #2ecc71; }
      .stat-value.negative { color: #e74c3c; }
      .stat-value.neutral { color: #888; }

      /* Faction Mechanic */
      .faction-mechanic {
        position: relative;
        text-align: center;
        padding-top: 15px;
        border-top: 1px solid #222;
        z-index: 1;
      }

      .mechanic-label {
        display: block;
        font-family: 'Share Tech Mono', monospace;
        font-size: 9px;
        color: #555;
        letter-spacing: 0.15em;
        margin-bottom: 5px;
      }

      .mechanic-name {
        font-family: 'Orbitron', sans-serif;
        font-size: 12px;
        font-weight: 500;
        color: var(--gold);
        letter-spacing: 0.1em;
      }

      .faction-card[data-faction="opus"] .mechanic-name { color: var(--opus-secondary); }
      .faction-card[data-faction="sonnet"] .mechanic-name { color: var(--sonnet-secondary); }
      .faction-card[data-faction="haiku"] .mechanic-name { color: var(--haiku-secondary); }

      /* Action Section */
      .action-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 25px;
        animation: actionEnter 0.8s ease-out 1.2s backwards;
      }

      @keyframes actionEnter {
        0% { opacity: 0; transform: translateY(20px); }
        100% { opacity: 1; transform: translateY(0); }
      }

      .selected-indicator {
        text-align: center;
      }

      .selected-label {
        display: block;
        font-family: 'Share Tech Mono', monospace;
        font-size: 10px;
        color: #555;
        letter-spacing: 0.2em;
        margin-bottom: 5px;
      }

      .selected-name {
        font-family: 'Orbitron', sans-serif;
        font-size: 16px;
        font-weight: 600;
        color: var(--gold);
        letter-spacing: 0.15em;
        transition: color 0.3s ease;
      }

      /* Buttons */
      .button-row {
        display: flex;
        gap: 20px;
      }

      .action-button {
        position: relative;
        padding: 16px 40px;
        background: transparent;
        border: none;
        cursor: pointer;
        overflow: hidden;
        transition: transform 0.2s ease;
      }

      .action-button:hover {
        transform: translateY(-2px);
      }

      .action-button:active {
        transform: translateY(1px);
      }

      .btn-bg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(180deg, rgba(30, 30, 40, 0.9) 0%, rgba(15, 15, 25, 0.95) 100%);
        clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
      }

      .btn-border {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: transparent;
        clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
        border: 2px solid var(--gold);
        box-sizing: border-box;
        transition: border-color 0.3s ease, box-shadow 0.3s ease;
      }

      .primary-button .btn-border {
        border-color: var(--gold);
      }

      .primary-button:hover .btn-border {
        box-shadow: 0 0 20px rgba(255, 215, 0, 0.4), inset 0 0 20px rgba(255, 215, 0, 0.1);
      }

      .continue-button .btn-border {
        border-color: #2ecc71;
      }

      .continue-button:hover .btn-border {
        box-shadow: 0 0 20px rgba(46, 204, 113, 0.4), inset 0 0 20px rgba(46, 204, 113, 0.1);
      }

      .btn-text {
        position: relative;
        font-family: 'Orbitron', sans-serif;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 0.15em;
        z-index: 1;
      }

      .primary-button .btn-text {
        color: var(--gold);
      }

      .continue-button .btn-text {
        color: #2ecc71;
      }

      .btn-shine {
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
        transition: left 0.5s ease;
      }

      .action-button:hover .btn-shine {
        left: 100%;
      }

      /* Version Info */
      .version-info {
        position: absolute;
        bottom: 20px;
        font-family: 'Share Tech Mono', monospace;
        font-size: 10px;
        color: #333;
        letter-spacing: 0.1em;
        display: flex;
        gap: 15px;
      }

      .version-info .separator {
        color: #222;
      }
    `
  }

  private initNeuralNetwork(): void {
    this.canvas = this.element.querySelector('#neural-canvas')
    if (!this.canvas) return

    this.ctx = this.canvas.getContext('2d')
    if (!this.ctx) return

    this.resizeCanvas()
    window.addEventListener('resize', () => this.resizeCanvas())

    // Create nodes
    const nodeCount = 60
    for (let i = 0; i < nodeCount; i++) {
      const factions = ['opus', 'sonnet', 'haiku']
      this.nodes.push({
        x: Math.random() * this.canvas.width,
        y: Math.random() * this.canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        faction: factions[Math.floor(Math.random() * 3)]
      })
    }
  }

  private resizeCanvas(): void {
    if (!this.canvas) return
    this.canvas.width = window.innerWidth
    this.canvas.height = window.innerHeight
  }

  private startAnimation(): void {
    const animate = () => {
      this.animationFrame = requestAnimationFrame(animate)
      this.drawNeuralNetwork()
    }
    animate()
  }

  private drawNeuralNetwork(): void {
    if (!this.ctx || !this.canvas) return

    const ctx = this.ctx
    const width = this.canvas.width
    const height = this.canvas.height

    // Clear
    ctx.fillStyle = 'rgba(3, 3, 5, 0.1)'
    ctx.fillRect(0, 0, width, height)

    const factionColors: Record<string, string> = {
      opus: 'rgba(155, 89, 182, ',
      sonnet: 'rgba(52, 152, 219, ',
      haiku: 'rgba(46, 204, 113, '
    }

    const selectedColor = factionColors[this.selectedFaction]

    // Update and draw nodes
    for (const node of this.nodes) {
      // Update position
      node.x += node.vx
      node.y += node.vy

      // Bounce off walls
      if (node.x < 0 || node.x > width) node.vx *= -1
      if (node.y < 0 || node.y > height) node.vy *= -1

      // Draw connections to nearby nodes
      for (const other of this.nodes) {
        const dx = other.x - node.x
        const dy = other.y - node.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (dist < 150 && dist > 0) {
          const alpha = (1 - dist / 150) * 0.3
          const color = node.faction === this.selectedFaction ? selectedColor : 'rgba(50, 50, 60, '
          ctx.beginPath()
          ctx.strokeStyle = color + alpha + ')'
          ctx.lineWidth = 1
          ctx.moveTo(node.x, node.y)
          ctx.lineTo(other.x, other.y)
          ctx.stroke()
        }
      }

      // Draw node
      const nodeAlpha = node.faction === this.selectedFaction ? 0.8 : 0.3
      const nodeSize = node.faction === this.selectedFaction ? 3 : 2
      ctx.beginPath()
      ctx.fillStyle = (node.faction === this.selectedFaction ? selectedColor : 'rgba(80, 80, 90, ') + nodeAlpha + ')'
      ctx.arc(node.x, node.y, nodeSize, 0, Math.PI * 2)
      ctx.fill()
    }
  }

  private setupEventListeners(screen: HTMLElement): void {
    const cards = screen.querySelectorAll('.faction-card')
    const selectedNameEl = screen.querySelector('#selected-faction-name')

    cards.forEach(card => {
      card.addEventListener('click', () => {
        cards.forEach(c => c.classList.remove('selected'))
        card.classList.add('selected')
        this.selectedFaction = card.getAttribute('data-faction')!

        if (selectedNameEl) {
          const faction = FACTIONS[this.selectedFaction]
          selectedNameEl.textContent = faction.name.toUpperCase()
        }
      })

      // Add hover sound effect visual cue
      card.addEventListener('mouseenter', () => {
        // Could trigger sound here
      })
    })

    const startBtn = screen.querySelector('#start-btn')!
    startBtn.addEventListener('click', () => {
      this.playExitAnimation().then(() => {
        this.onStart(this.selectedFaction)
      })
    })

    const continueBtn = screen.querySelector('#continue-btn')
    if (continueBtn && this.onContinue) {
      continueBtn.addEventListener('click', () => {
        this.playExitAnimation().then(() => {
          this.onContinue!()
        })
      })
    }
  }

  private playExitAnimation(): Promise<void> {
    return new Promise(resolve => {
      this.element.style.transition = 'opacity 0.5s ease, transform 0.5s ease'
      this.element.style.opacity = '0'
      this.element.style.transform = 'scale(1.05)'
      setTimeout(resolve, 500)
    })
  }

  private getStatsHTML(faction: Faction): string {
    const stats = [
      { label: 'POWER', value: faction.bonuses.unitPower ?? 1 },
      { label: 'SPEED', value: faction.bonuses.unitSpeed ?? 1 },
      { label: 'RESEARCH', value: faction.bonuses.researchSpeed ?? 1 },
      { label: 'ECONOMY', value: faction.bonuses.gatherSpeed ?? 1 },
      { label: 'CAPACITY', value: faction.bonuses.populationCap ?? 1 }
    ]

    return stats.map(stat => {
      const percent = Math.round((stat.value - 1) * 100)
      const width = Math.min(100, Math.max(0, 50 + percent))
      const isPositive = percent > 0
      const isNegative = percent < 0
      const barClass = isPositive ? 'positive' : isNegative ? 'negative' : 'neutral'
      const valueClass = barClass
      const displayValue = percent > 0 ? `+${percent}%` : percent < 0 ? `${percent}%` : '—'

      return `
        <div class="stat-row">
          <span class="stat-label">${stat.label}</span>
          <div class="stat-bar-container">
            <div class="stat-bar ${barClass}" style="width: ${width}%"></div>
          </div>
          <span class="stat-value ${valueClass}">${displayValue}</span>
        </div>
      `
    }).join('')
  }

  private getMechanicName(mechanic: string): string {
    const names: Record<string, string> = {
      extendedThinking: 'DEEP COGNITION',
      contextBonus: 'ADAPTIVE MATRIX',
      batchProduction: 'SWARM PROTOCOL'
    }
    return names[mechanic] || mechanic.toUpperCase()
  }

  private getHue(color: number): number {
    const r = (color >> 16) & 255
    const g = (color >> 8) & 255
    const b = color & 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)

    if (max === min) return 0

    let h = 0
    const d = max - min

    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }

    return Math.round(h * 360)
  }

  private colorToCSS(color: number): string {
    const hex = color.toString(16).padStart(6, '0')
    return `#${hex}`
  }

  hide(): void {
    this.element.style.display = 'none'
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
    }
  }

  show(): void {
    this.element.style.display = 'flex'
    this.element.style.opacity = '1'
    this.element.style.transform = 'scale(1)'
    this.startAnimation()
  }

  destroy(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
    }
    const style = document.getElementById('start-screen-styles')
    if (style) style.remove()
    this.element.remove()
  }
}
